import React, { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import classNames from 'classnames'

import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { UserRelationshipType } from '@web/gql/graphql'
import useMe from '@web/hooks/useMe'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { messages } from '@web/libs/constants'

import UserRelationshipList from './UserRelationshipList'
import styles from './UserRelationshipRequests.module.scss'

export const UserRelationshipRequestsQuery = gql(`
  query UserRelationshipRequests($idno: String!, $type: UserRelationshipType!, $after: String) {
    userRelationships(input: { after: $after, type: $type, idno: $idno }) {
      ...UserRelationshipPage
    }
  }
`)

interface Props {
  type: UserRelationshipType.Following | UserRelationshipType.Follower
}

const UserRelationshipRequests = ({ type }: Props) => {
  const { me } = useMe()

  const isFollowerType = type === UserRelationshipType.Follower

  const internalRelationshipType = isFollowerType
    ? UserRelationshipType.FollowRequester
    : UserRelationshipType.FollowRequest

  const { data, loading, fetchMore } = useQuery(UserRelationshipRequestsQuery, {
    variables: {
      idno: me!.idno,
      type: internalRelationshipType,
    },
    fetchPolicy: 'cache-and-network',
    onError: () => {
      openErrorToast(isFollowerType ? messages.ERR_GET_FOLLOW_REQUESTERS : messages.ERR_GET_FOLLOW_REQUESTS)
    },
  })

  const { UserRelationshipsSucceed } = extractByTypename(data?.userRelationships)

  const fetchMoreRelationships = () => {
    if (!UserRelationshipsSucceed) return

    fetchMore({
      variables: {
        after: UserRelationshipsSucceed.connection.pageInfo.endCursor,
      },
    })
  }

  const users = UserRelationshipsSucceed?.connection.edges.map(({ user }) => user) ?? []
  const hasMoreCursor = Number(UserRelationshipsSucceed?.connection.pageInfo.endCursor) > 0

  const requestCount = useMemo(() => {
    const requestCountMap = {
      [UserRelationshipType.FollowRequest]: me?.stat.followRequestCount ?? 0,
      [UserRelationshipType.FollowRequester]: me?.stat.followRequesterCount ?? 0,
    }
    return requestCountMap[internalRelationshipType]
  }, [internalRelationshipType, me])

  if (requestCount === 0) return null

  return (
    <div className={classNames('place_section', styles.follow_apply_area)}>
      <div className={styles.inner}>
        <p className={styles.desc}>
          {isFollowerType ? `${requestCount}명의 팔로우 신청이 있습니다.` : '내가 팔로우 신청한 리뷰어'}
        </p>
        <UserRelationshipList
          fetchMore={fetchMoreRelationships}
          loading={loading}
          totalUsers={requestCount}
          users={users}
          type={internalRelationshipType}
          hasMoreCursor={hasMoreCursor}
        />
      </div>
    </div>
  )
}

export default UserRelationshipRequests
